import api from './api';
import type {
  LoginRequest,
  LoginResponse,
  User,
  InsurerLoginResponse,
  BrokerLoginResponse,
  ChannelLoginResponse,
  CustomerLoginResponse,
  OTPVerifyResponse,
  BrokerOption,
} from '@/types';

export const authService = {
  // Generic login (admin/platform users)
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/users/auth/login/', credentials);
    return response.data;
  },

  // Role-specific logins
  async insurerLogin(credentials: LoginRequest): Promise<InsurerLoginResponse> {
    const response = await api.post<InsurerLoginResponse>('/users/auth/insurer/login/', credentials);
    return response.data;
  },

  async brokerLogin(credentials: LoginRequest): Promise<BrokerLoginResponse> {
    const response = await api.post<BrokerLoginResponse>('/users/auth/broker/login/', credentials);
    return response.data;
  },

  async channelLogin(credentials: LoginRequest): Promise<ChannelLoginResponse> {
    const response = await api.post<ChannelLoginResponse>('/users/auth/channel/login/', credentials);
    return response.data;
  },

  async customerLogin(credentials: LoginRequest): Promise<CustomerLoginResponse> {
    const response = await api.post<CustomerLoginResponse>('/users/auth/customer/login/', credentials);
    return response.data;
  },

  // 9c: no argument any more -- the refresh token is an httpOnly cookie the
  // browser attaches itself. The server blacklists it and clears the cookie.
  async logout(): Promise<void> {
    try {
      await api.post('/users/auth/logout/', {});
    } catch {
      // Ignore logout errors - the local session is cleared regardless
    }
  },

  async refreshToken(): Promise<{ access: string }> {
    const response = await api.post('/users/auth/refresh/', {});
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/users/me/');
    return response.data;
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.patch<User>('/users/me/', data);
    return response.data;
  },

  async changePassword(data: {
    old_password: string;
    new_password: string;
    new_password_confirm: string;
  }): Promise<void> {
    await api.post('/users/change_password/', data);
  },

  async requestOtp(identifier: string, purpose: string = 'login'): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/users/otp/request/', { identifier, purpose });
    return response.data;
  },

  async verifyOtp(identifier: string, otp_code: string, purpose: string = 'login'): Promise<OTPVerifyResponse> {
    const response = await api.post<OTPVerifyResponse>('/users/otp/verify/', { identifier, otp_code, purpose });
    return response.data;
  },

  async selectBroker(brokerId: string): Promise<{ message: string; broker: BrokerOption }> {
    const response = await api.post('/users/auth/select-broker/', { broker_id: brokerId });
    return response.data;
  },

  async getMyBrokers(): Promise<BrokerOption[]> {
    const response = await api.get<BrokerOption[]>('/users/auth/my-brokers/');
    return response.data;
  },
};
